import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Copy, 
  Check, 
  Twitter, 
  Facebook, 
  Instagram,
  Youtube,
  Video,
  Image as ImageIcon,
  FileText,
  Download,
  Sparkles,
  Zap,
  Eye,
  Camera,
  ChevronLeft,
  Aperture
} from "lucide-react";
import { toast } from "sonner";

const Marketing = () => {
  const navigate = useNavigate();
  const [copiedId, setCopiedId] = useState(null);

  const appUrl = window.location.origin;
  
  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    toast.success("Copied to clipboard!");
    setTimeout(() => setCopiedId(null), 2000);
  };

  const adContent = {
    tiktok: [
      {
        id: "tiktok1",
        hook: "POV: You finally see what's REALLY in the sky 👁️",
        script: `POV: You finally see what's REALLY in the sky 👁️

I found this app called Skybot that uses AI to detect anomalies in the sky...

And bro... the stuff it finds is INSANE 🛸

It has a sun dimming filter so you can actually see what's near the sun without going blind

Plus night mode that makes stars POP

And the AI analysis tells you exactly what it detects

Link in bio if you want to try it 👀

#ufo #alien #skywatch #conspiracy #aetherlens #ai`,
        hashtags: "#ufo #alien #skywatch #conspiracy #aetherlens #ai #fyp #viral"
      },
      {
        id: "tiktok2", 
        hook: "They don't want you to see this... 🤫",
        script: `They don't want you to see this... 🤫

This AI app just exposed something in the sky that I can't explain

It's called Skybot and it literally analyzes the sky for anomalies

Watch what happens when I point it at the sun with the dimming filter on...

WHAT IS THAT?! 

The AI detected something with 87% confidence...

I'm not saying it's aliens but... 👽

Link in bio - see for yourself

#whatisthat #ufoevidence #skyanomaly #aetherlens`,
        hashtags: "#whatisthat #ufoevidence #skyanomaly #aetherlens #fyp"
      },
      {
        id: "tiktok3",
        hook: "Why is no one talking about this app?! 🔥",
        script: `Why is no one talking about this app?! 🔥

Skybot uses GPT-4 AI to scan the sky and detect:
- UFOs and unknown objects
- Cloaking technology distortions  
- Atmospheric anomalies
- Hidden celestial objects

The night mode + star enhancement is CRAZY for moon shots 🌙

And the 4K upscaling makes everything crystal clear

This is the future of sky watching fr fr

#aetherlens #aiapp #skywatch #ufohunting`,
        hashtags: "#aetherlens #aiapp #skywatch #ufohunting #tech #fyp"
      }
    ],
    instagram: [
      {
        id: "ig1",
        type: "Reel Caption",
        content: `👁️ WHAT DID I JUST CAPTURE?!

Using Skybot AI to scan the sky and... I'm speechless 🛸

This app uses GPT-4 vision to detect anomalies that our eyes can't see:
✨ Sun dimming filter (finally see what's NEAR the sun)
🌙 Night mode (stars like never before)
🔍 AI anomaly detection
📸 4K enhancement

The future of sky watching is HERE

Link in bio 👆

#aetherlens #ufo #skywatch #aitech #anomaly #whatisthat #unexplained #nightsky #astronomy #conspiracy #disclosure`,
      },
      {
        id: "ig2",
        type: "Story Caption",
        content: `SWIPE UP to see what the AI found in my sky 👁️🛸

Skybot - AI Sky Anomaly Detection

Link in bio!`,
      },
      {
        id: "ig3",
        type: "Post Caption",
        content: `The truth is up there. Now we can finally SEE it. 👁️

Introducing Skybot - AI-powered sky anomaly detection that reveals what's hidden in plain sight.

🔬 GPT-4 Vision AI Analysis
☀️ Sun Dimming Technology  
🌟 4K Star Enhancement
🛸 Real-time Anomaly Detection

What will YOU discover?

#Skybot #SkyAnomaly #UFOHunting #AITech #NightSky #Astronomy #Disclosure #WakeUp`,
      }
    ],
    youtube: [
      {
        id: "yt1",
        type: "Video Title",
        content: "I Used AI to Scan the Sky... What It Found Will SHOCK You 👁️🛸"
      },
      {
        id: "yt2", 
        type: "Video Description",
        content: `🛸 WHAT IS AETHER LENS?
Skybot is an AI-powered sky anomaly detection app that uses GPT-4 vision to analyze the sky in real-time.

⚡ FEATURES:
- Live camera sky scanning
- Sun dimming filter (see what's near the sun!)
- Night mode enhancement
- Star enhancement & 4K upscaling
- AI anomaly detection with confidence scores
- Detection history & gallery

🔗 TRY IT NOW: ${appUrl}

👁️ WHAT CAN IT DETECT?
- Unidentified flying objects
- Unusual light patterns
- Atmospheric anomalies
- Cloaking technology distortions
- Hidden celestial objects

📱 Works on any device with a camera!

🏷️ TAGS:
ufo, uap, sky anomaly, ai detection, gpt-4, aether lens, sky watching, night sky, astronomy, unexplained, disclosure, aliens

#UFO #SkyAnomaly #AI #Skybot`
      },
      {
        id: "yt3",
        type: "Shorts Script",
        content: `[HOOK - 0:00]
"This AI just found something in the sky that I can't explain..."

[DEMO - 0:03]
*Show pointing phone at sky*
"I'm using Skybot - it scans the sky with AI"

[REVEAL - 0:08]
*Show detection results*
"And look what it detected... 87% confidence"

[CTA - 0:12]
"Link in description if you want to try it"
*Show app URL*`
      }
    ],
    facebook: [
      {
        id: "fb1",
        type: "Post",
        content: `🛸 Have you ever wondered what's REALLY in the sky?

I found this incredible AI app called Skybot that analyzes the sky in real-time and detects anomalies that our eyes can't see.

Features:
☀️ Sun dimming filter - finally see what's near the sun
🌙 Night mode - enhanced star visibility
🤖 AI analysis - tells you exactly what it detects
📸 4K enhancement - crystal clear images

The results have been... interesting 👀

Try it yourself: ${appUrl}

What do you think is up there? 👇`
      },
      {
        id: "fb2",
        type: "Group Post",
        content: `Hey everyone! 👋

Just wanted to share this AI sky scanner I've been using called Skybot.

It uses GPT-4 vision to detect anomalies in the sky - UFOs, unusual lights, atmospheric disturbances, etc.

The sun dimming filter is incredible for capturing what's actually near the sun without damaging your camera.

Has anyone else tried it? Would love to see what you find!

Link: ${appUrl}

#SkyWatch #UFO #Skybot`
      }
    ],
    twitter: [
      {
        id: "tw1",
        content: `🧵 THREAD: I found an AI app that detects anomalies in the sky and I'm not okay

It's called Skybot and it uses GPT-4 vision to analyze what's above us

Here's what I found... 👇🛸`
      },
      {
        id: "tw2",
        content: `What if I told you there's an app that can see what your eyes can't? 👁️

Skybot - AI-powered sky anomaly detection

• Sun dimming filter
• Night mode enhancement  
• Real-time AI analysis
• 4K upscaling

The truth is up there 🛸

${appUrl}`
      },
      {
        id: "tw3",
        content: `POV: You use AI to scan the sky and it detects something with 87% confidence 

*visible confusion*

Try Skybot - link in bio 👁️🛸

#UFO #AI #SkyWatch`
      }
    ],
    reddit: [
      {
        id: "reddit1",
        subreddit: "r/UFOs, r/HighStrangeness, r/conspiracy",
        title: "I built an AI app that detects sky anomalies - here's what it found",
        content: `Hey everyone,

I've been working on an AI-powered sky scanner called Skybot that uses GPT-4 vision to detect anomalies.

**Features:**
- Real-time sky analysis
- Sun dimming filter (see what's near the sun without the glare)
- Night mode enhancement
- Anomaly detection with confidence scores
- 4K AI upscaling

I've been testing it for a few weeks and some of the detections have been... interesting.

Would love to get the community's thoughts. You can try it here: ${appUrl}

What do you think? Has anyone else used AI for sky watching?`
      }
    ]
  };

  const videoScript = `
=== AETHER LENS - 30 SECOND AD SCRIPT ===

[0:00 - HOOK]
*Dark sky footage*
NARRATOR: "What if your eyes have been lying to you?"

[0:03 - PROBLEM]  
*Person looking at sky, squinting*
NARRATOR: "The sky holds secrets we can't see..."

[0:06 - SOLUTION]
*Phone screen showing Skybot app*
NARRATOR: "Until now. Introducing Skybot."

[0:09 - FEATURES]
*Quick cuts of app features*
NARRATOR: "AI-powered sky detection.
Sun dimming technology.
4K enhancement.
Real-time anomaly analysis."

[0:18 - DEMO]
*Show detection happening*
NARRATOR: "See what's really up there."

[0:22 - SOCIAL PROOF]
*Detection results with high confidence*
TEXT ON SCREEN: "87% Confidence - Anomaly Detected"

[0:25 - CTA]
*App logo + URL*
NARRATOR: "Skybot. The truth is up there."
TEXT: "Try free at aetherlens.app"

[0:28 - END CARD]
*Logo + QR code*

=== END SCRIPT ===
`;

  return (
    <div className="min-h-screen bg-[#02040a]">
      {/* Noise Overlay */}
      <div className="noise-overlay" />

      {/* Header */}
      <header className="border-b border-white/5 p-6">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Aperture className="w-8 h-8 text-[#00f2ea]" />
            <span className="font-unbounded font-bold text-white text-xl">Marketing Hub</span>
          </div>
          <button
            onClick={() => navigate('/dashboard')}
            className="btn-secondary flex items-center gap-2"
          >
            <ChevronLeft className="w-4 h-4" />
            Back to App
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto p-6 lg:p-12">
        {/* Hero */}
        <div className="text-center mb-16">
          <h1 className="font-unbounded font-black text-4xl md:text-5xl text-white mb-4">
            Ready-to-Use <span className="text-[#00f2ea]">Ad Content</span>
          </h1>
          <p className="font-exo2 text-slate-400 text-lg max-w-2xl mx-auto">
            Copy these viral ad scripts and post them on TikTok, Instagram, YouTube, Facebook, Twitter, and Reddit.
          </p>
        </div>

        {/* TikTok Section */}
        <section className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-black rounded-lg flex items-center justify-center">
              <Video className="w-6 h-6 text-white" />
            </div>
            <h2 className="font-unbounded font-bold text-2xl text-white">TikTok Scripts</h2>
          </div>

          <div className="grid gap-6">
            {adContent.tiktok.map((ad, i) => (
              <div key={ad.id} className="glass-panel p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <span className="text-[#00f2ea] font-mono text-sm">Script #{i + 1}</span>
                    <h3 className="font-unbounded font-bold text-white mt-1">{ad.hook}</h3>
                  </div>
                  <button
                    onClick={() => copyToClipboard(ad.script, ad.id)}
                    className="btn-secondary px-3 py-2 flex items-center gap-2"
                  >
                    {copiedId === ad.id ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                    Copy
                  </button>
                </div>
                <pre className="font-mono text-sm text-slate-300 whitespace-pre-wrap bg-[#02040a] p-4 rounded-lg overflow-x-auto">
                  {ad.script}
                </pre>
              </div>
            ))}
          </div>
        </section>

        {/* Instagram Section */}
        <section className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 via-pink-500 to-orange-500 rounded-lg flex items-center justify-center">
              <Instagram className="w-6 h-6 text-white" />
            </div>
            <h2 className="font-unbounded font-bold text-2xl text-white">Instagram Captions</h2>
          </div>

          <div className="grid gap-6">
            {adContent.instagram.map((ad) => (
              <div key={ad.id} className="glass-panel p-6">
                <div className="flex items-start justify-between mb-4">
                  <span className="px-3 py-1 bg-purple-500/20 text-purple-400 rounded-full text-sm font-mono">
                    {ad.type}
                  </span>
                  <button
                    onClick={() => copyToClipboard(ad.content, ad.id)}
                    className="btn-secondary px-3 py-2 flex items-center gap-2"
                  >
                    {copiedId === ad.id ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                    Copy
                  </button>
                </div>
                <pre className="font-mono text-sm text-slate-300 whitespace-pre-wrap bg-[#02040a] p-4 rounded-lg">
                  {ad.content}
                </pre>
              </div>
            ))}
          </div>
        </section>

        {/* YouTube Section */}
        <section className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center">
              <Youtube className="w-6 h-6 text-white" />
            </div>
            <h2 className="font-unbounded font-bold text-2xl text-white">YouTube Content</h2>
          </div>

          <div className="grid gap-6">
            {adContent.youtube.map((ad) => (
              <div key={ad.id} className="glass-panel p-6">
                <div className="flex items-start justify-between mb-4">
                  <span className="px-3 py-1 bg-red-500/20 text-red-400 rounded-full text-sm font-mono">
                    {ad.type}
                  </span>
                  <button
                    onClick={() => copyToClipboard(ad.content, ad.id)}
                    className="btn-secondary px-3 py-2 flex items-center gap-2"
                  >
                    {copiedId === ad.id ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                    Copy
                  </button>
                </div>
                <pre className="font-mono text-sm text-slate-300 whitespace-pre-wrap bg-[#02040a] p-4 rounded-lg">
                  {ad.content}
                </pre>
              </div>
            ))}
          </div>
        </section>

        {/* Twitter Section */}
        <section className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-[#1DA1F2] rounded-lg flex items-center justify-center">
              <Twitter className="w-6 h-6 text-white" />
            </div>
            <h2 className="font-unbounded font-bold text-2xl text-white">Twitter/X Posts</h2>
          </div>

          <div className="grid gap-6">
            {adContent.twitter.map((ad, i) => (
              <div key={ad.id} className="glass-panel p-6">
                <div className="flex items-start justify-between mb-4">
                  <span className="px-3 py-1 bg-[#1DA1F2]/20 text-[#1DA1F2] rounded-full text-sm font-mono">
                    Tweet #{i + 1}
                  </span>
                  <button
                    onClick={() => copyToClipboard(ad.content, ad.id)}
                    className="btn-secondary px-3 py-2 flex items-center gap-2"
                  >
                    {copiedId === ad.id ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                    Copy
                  </button>
                </div>
                <pre className="font-mono text-sm text-slate-300 whitespace-pre-wrap bg-[#02040a] p-4 rounded-lg">
                  {ad.content}
                </pre>
              </div>
            ))}
          </div>
        </section>

        {/* Facebook Section */}
        <section className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-[#4267B2] rounded-lg flex items-center justify-center">
              <Facebook className="w-6 h-6 text-white" />
            </div>
            <h2 className="font-unbounded font-bold text-2xl text-white">Facebook Posts</h2>
          </div>

          <div className="grid gap-6">
            {adContent.facebook.map((ad) => (
              <div key={ad.id} className="glass-panel p-6">
                <div className="flex items-start justify-between mb-4">
                  <span className="px-3 py-1 bg-[#4267B2]/20 text-[#4267B2] rounded-full text-sm font-mono">
                    {ad.type}
                  </span>
                  <button
                    onClick={() => copyToClipboard(ad.content, ad.id)}
                    className="btn-secondary px-3 py-2 flex items-center gap-2"
                  >
                    {copiedId === ad.id ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                    Copy
                  </button>
                </div>
                <pre className="font-mono text-sm text-slate-300 whitespace-pre-wrap bg-[#02040a] p-4 rounded-lg">
                  {ad.content}
                </pre>
              </div>
            ))}
          </div>
        </section>

        {/* Reddit Section */}
        <section className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-[#FF4500] rounded-lg flex items-center justify-center">
              <span className="text-white font-bold">r/</span>
            </div>
            <h2 className="font-unbounded font-bold text-2xl text-white">Reddit Posts</h2>
          </div>

          <div className="grid gap-6">
            {adContent.reddit.map((ad) => (
              <div key={ad.id} className="glass-panel p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <span className="px-3 py-1 bg-[#FF4500]/20 text-[#FF4500] rounded-full text-sm font-mono">
                      {ad.subreddit}
                    </span>
                    <h3 className="font-unbounded font-bold text-white mt-2">{ad.title}</h3>
                  </div>
                  <button
                    onClick={() => copyToClipboard(`${ad.title}\n\n${ad.content}`, ad.id)}
                    className="btn-secondary px-3 py-2 flex items-center gap-2"
                  >
                    {copiedId === ad.id ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                    Copy
                  </button>
                </div>
                <pre className="font-mono text-sm text-slate-300 whitespace-pre-wrap bg-[#02040a] p-4 rounded-lg">
                  {ad.content}
                </pre>
              </div>
            ))}
          </div>
        </section>

        {/* Video Script Section */}
        <section className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-[#ffd700] rounded-lg flex items-center justify-center">
              <Video className="w-6 h-6 text-[#02040a]" />
            </div>
            <h2 className="font-unbounded font-bold text-2xl text-white">30-Second Video Ad Script</h2>
          </div>

          <div className="glass-panel p-6">
            <div className="flex items-start justify-between mb-4">
              <p className="font-exo2 text-slate-400">
                Use this script to create a professional video ad for any platform
              </p>
              <button
                onClick={() => copyToClipboard(videoScript, 'video')}
                className="btn-secondary px-3 py-2 flex items-center gap-2"
              >
                {copiedId === 'video' ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                Copy Script
              </button>
            </div>
            <pre className="font-mono text-sm text-slate-300 whitespace-pre-wrap bg-[#02040a] p-4 rounded-lg overflow-x-auto">
              {videoScript}
            </pre>
          </div>
        </section>

        {/* Quick Tips */}
        <section className="glass-panel p-8">
          <h2 className="font-unbounded font-bold text-2xl text-white mb-6 flex items-center gap-3">
            <Sparkles className="w-6 h-6 text-[#ffd700]" />
            Pro Tips for Viral Ads
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h3 className="font-exo2 font-bold text-[#00f2ea]">TikTok</h3>
              <ul className="space-y-2 font-exo2 text-slate-300 text-sm">
                <li>• Post during peak hours (7-9pm)</li>
                <li>• Use trending sounds</li>
                <li>• Hook viewers in first 1 second</li>
                <li>• Show actual detections for credibility</li>
              </ul>
            </div>
            <div className="space-y-4">
              <h3 className="font-exo2 font-bold text-[#00f2ea]">Instagram</h3>
              <ul className="space-y-2 font-exo2 text-slate-300 text-sm">
                <li>• Use Reels for maximum reach</li>
                <li>• Post to Stories daily</li>
                <li>• Use relevant UFO/astronomy hashtags</li>
                <li>• Engage with UFO communities</li>
              </ul>
            </div>
            <div className="space-y-4">
              <h3 className="font-exo2 font-bold text-[#00f2ea]">YouTube</h3>
              <ul className="space-y-2 font-exo2 text-slate-300 text-sm">
                <li>• Create Shorts for quick virality</li>
                <li>• Make longer "investigation" videos</li>
                <li>• Collaborate with UFO channels</li>
                <li>• SEO optimize titles and descriptions</li>
              </ul>
            </div>
            <div className="space-y-4">
              <h3 className="font-exo2 font-bold text-[#00f2ea]">Reddit</h3>
              <ul className="space-y-2 font-exo2 text-slate-300 text-sm">
                <li>• Be authentic, not salesy</li>
                <li>• Engage in comments</li>
                <li>• Post in r/UFOs, r/HighStrangeness</li>
                <li>• Share interesting findings</li>
              </ul>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default Marketing;
